import isSafeRelativePath from '@/utils/urlSafety'
import { providerLabel } from '@/pages/integrations/utils/providerLabels'
import { getProviderOverride, overrideKeyForSource } from './overrides'

const SETUP_ROUTE_TOKEN = '{connection_id}'

/** Same base as the API client (`src/api/client.js`). */
function apiBase() {
  return import.meta.env.VITE_API_URL || '/api'
}

/**
 * The backend-served logo for a provider key (never hotlinked).
 *
 * @param {string|null|undefined} key
 * @returns {string|null}
 */
export function providerLogoUrl(key) {
  if (!key) return null
  return `${apiBase()}/integrations/providers/${encodeURIComponent(key)}/logo/`
}

/**
 * The catalog's `logo_url` when it points at our API or a same-origin path,
 * otherwise the served logo, so no `<img>` ever loads from a third-party host.
 *
 * @param {unknown} logoUrl
 * @param {string} id - provider key
 * @returns {string|null}
 */
function servedLogoUrl(logoUrl, id) {
  const base = apiBase()
  const apiPrefix = base.endsWith('/') ? base : `${base}/`
  if (typeof logoUrl === 'string' && (logoUrl.startsWith(apiPrefix) || isSafeRelativePath(logoUrl))) return logoUrl
  return providerLogoUrl(id)
}

/**
 * One `/integrations/providers/` resource, flattened (snake_case kept, like
 * `normalizeResource`), with every field a component reads given a default.
 *
 * @param {Object} resource - `{id, type, attributes}`
 * @returns {Object}
 */
export function normalizeProvider(resource) {
  const attributes = resource?.attributes || {}
  const id = resource?.id
  const sourceLabels = attributes.source_type_labels
  return {
    ...attributes,
    id,
    label: attributes.label || attributes.name || id,
    on_disconnect: attributes.on_disconnect || 'soft',
    source_types: Array.isArray(attributes.source_types) ? attributes.source_types : [],
    source_type_labels: sourceLabels && typeof sourceLabels === 'object' && !Array.isArray(sourceLabels)
      ? Object.fromEntries(Object.entries(sourceLabels).filter(([, label]) => typeof label === 'string' && label.trim()).map(([type, label]) => [type, label.trim()]))
      : {},
    capabilities: Array.isArray(attributes.capabilities) ? attributes.capabilities : [],
    logo_url: servedLogoUrl(attributes.logo_url, id),
  }
}

/**
 * The in-app path a catalog `setup_route` template names for a connection,
 * or null when the template is missing, malformed or unsafe.
 *
 * @param {unknown} template - e.g. `/setup/acme/{connection_id}`
 * @param {string} connectionId
 * @returns {string|null}
 */
export function formatSetupRoute(template, connectionId) {
  if (typeof template !== 'string' || !template.includes(SETUP_ROUTE_TOKEN) || !connectionId) return null
  const path = template.replace(SETUP_ROUTE_TOKEN, encodeURIComponent(connectionId))
  return isSafeRelativePath(path) ? path : null
}

/**
 * The guided setup of one of a provider's connections: the catalog
 * `setup_route` (through `formatSetupRoute`'s guards), else the override's
 * own `setupPath` (Planning Center while the catalog is down), else null.
 *
 * @param {string|null|undefined} providerKey
 * @param {Object[]} providers - normalized catalog entries
 * @param {string} connectionId
 * @returns {string|null}
 */
export function setupPathFor(providerKey, providers, connectionId) {
  if (!providerKey || !connectionId) return null
  const listed = providers.find((provider) => provider.id === providerKey)
  const fromCatalog = formatSetupRoute(listed?.setup_route, connectionId)
  if (fromCatalog) return fromCatalog
  const override = getProviderOverride(providerKey)
  return override?.setupPath ? override.setupPath(connectionId) : null
}

/**
 * `providers` plus one generic entry per provider that still has a
 * connection but is missing from the catalog (flagged off after linking), so
 * that connection keeps a card it can be disconnected from.
 *
 * @param {Object[]} providers
 * @param {Object[]} connections
 * @returns {Object[]}
 */
export function withUnlistedProviders(providers, connections) {
  const seen = new Set(providers.map((provider) => provider.id))
  const unlisted = []
  for (const connection of connections || []) {
    const key = connection?.provider
    if (!key || seen.has(key)) continue
    seen.add(key)
    const label = providerLabel(key)
    unlisted.push({
      id: key,
      name: label,
      label,
      on_disconnect: 'soft',
      source_types: [],
      capabilities: [],
      logo_url: providerLogoUrl(key),
    })
  }
  return unlisted.length ? [...providers, ...unlisted] : providers
}

/**
 * The static onboarding answers plus each catalog ChMS whose
 * `onboarding_source` isn't already one of them (first one wins).
 *
 * @param {Array<{key: string, label: string, description: string}>} staticSources
 * @param {Object[]} providers
 * @returns {Array<{key: string, label: string, description: string, providerKey?: string}>}
 */
export function withCatalogSources(staticSources, providers) {
  const seen = new Set(staticSources.map((source) => source.key))
  const extra = []
  for (const provider of providers) {
    const source = provider.onboarding_source
    if (provider.kind !== 'chms' || !source || seen.has(source)) continue
    seen.add(source)
    extra.push({ key: source, label: provider.label, description: provider.description || '', providerKey: provider.id })
  }
  return extra.length ? [...staticSources, ...extra] : staticSources
}

/**
 * @param {string|null|undefined} source
 * @param {Object[]} providers
 * @returns {string|null}
 */
export function providerKeyForSource(source, providers) {
  if (!source) return null
  const listed = providers.find((provider) => provider.onboarding_source === source)
  return listed ? listed.id : overrideKeyForSource(source)
}
